import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { topicsApi, commentsApi, authApi } from '../services/api';
import { supabase, exchangeCodeIfPresent } from '../lib/supabase';
import { Topic, Comment, User, BasemapId, TopicVisibility } from '../types';

// Custom hook for topics
export const useTopics = (params?: {
  page?: number;
  limit?: number;
  sortBy?: 'popular' | 'recent' | 'oldest' | 'active';
  country?: string;
  region?: string;
  city?: string;
  language?: string;
  search?: string;
  bbox?: string;
}) => {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<any>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Keep a local cache of user-soft-deleted topics so they remain visible as [deleted]
  const softDeletedTopicsRef = useRef<Record<string, Topic>>({});

  // Memoize params to prevent unnecessary re-renders
  const memoizedParams = useMemo(() => {
    if (!params) return undefined;
    return { ...params };
  }, [params]);

  const fetchTopics = useCallback(async () => {
    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      setError(null);
      const response = await topicsApi.getTopics(memoizedParams);

      // Only update state if request wasn't aborted
      if (!abortControllerRef.current.signal.aborted) {
        // Merge in locally soft-deleted topics (user deletions) that backend may omit
        const incoming = response.data;
        const softDeleted = Object.values(softDeletedTopicsRef.current || {});
        const merged = softDeleted.length
          ? [
            ...incoming,
            ...softDeleted.filter(sd => !incoming.some(t => t.id === sd.id))
          ]
          : incoming;
        setTopics(merged);
        setMeta(response.meta);
      }
    } catch (err: any) {
      // Ignore aborted requests
      if (err.name === 'AbortError' || abortControllerRef.current?.signal.aborted) {
        return;
      }

      if (err.response?.status === 429) {
        setError('Too many requests. Using cached data. Please wait a moment before searching again.');
        console.warn('Rate limited - using cached topics');
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to fetch topics');
        console.error('Error fetching topics:', err);
      }
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setLoading(false);
      }
    }
  }, [memoizedParams]);

  useEffect(() => {
    // Add a small delay to prevent rapid-fire requests
    const timeoutId = setTimeout(() => {
      fetchTopics();
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchTopics]);

  const createTopic = async (topicData: {
    title: string;
    content: string;
    latitude?: number;
    longitude?: number;
    country?: string;
    region?: string;
    city?: string;
    language?: string;
    basemap?: BasemapId;
    visibility?: TopicVisibility;
    audienceUserIds?: string[];
    isNsfw?: boolean;
    isEditorial?: boolean;
    isAnonymous?: boolean;
  }) => {
    try {
      const newTopic = await topicsApi.createTopic(topicData);
      setTopics(prev => [newTopic, ...prev]);
      return newTopic;
    } catch (err: any) {
      console.error('Error creating topic:', err);
      throw err;
    }
  };

  const markTopicSoftDeleted = (topic: Topic) => {
    const masked: Topic = {
      ...topic,
      isDeleted: true,
      deletedBy: 'USER',
      deletedAt: new Date().toISOString(),
      user: { ...topic.user, isDeleted: true },
    };
    // Update in list or prepend if missing
    setTopics(prev => {
      const exists = prev.some(t => t.id === topic.id);
      const next = exists ? prev.map(t => (t.id === topic.id ? masked : t)) : [masked, ...prev];
      return next;
    });
    // Cache for merge on future refetches
    softDeletedTopicsRef.current[topic.id] = masked;
  };

  const voteTopic = async (topicId: string, voteType: 'UPVOTE' | 'DOWNVOTE') => {
    try {
      const updatedTopic = await topicsApi.voteTopic(topicId, voteType);
      setTopics(prev =>
        prev.map(topic => topic.id === topicId ? updatedTopic : topic)
      );
      return updatedTopic;
    } catch (err: any) {
      console.error('Error voting on topic:', err);
      throw err;
    }
  };

  const updateTopic = async (topicId: string, updateData: {
    title?: string;
    content?: string;
    latitude?: number;
    longitude?: number;
    country?: string;
    region?: string;
    city?: string;
    basemap?: BasemapId;
    visibility?: TopicVisibility;
    audienceUserIds?: string[];
    isNsfw?: boolean;
    isEditorial?: boolean;
  }) => {
    try {
      const updatedTopic = await topicsApi.updateTopic(topicId, updateData);
      setTopics(prev =>
        prev.map(topic => topic.id === topicId ? updatedTopic : topic)
      );
      return updatedTopic;
    } catch (err: any) {
      console.error('Error updating topic:', err);
      throw err;
    }
  };

  const deleteTopic = async (topicId: string) => {
    try {
      await topicsApi.deleteTopic(topicId);
      // Soft-delete in local state so the topic remains visible with [deleted] author
      let snapshot: Topic | undefined;
      setTopics(prev => prev.map(topic => {
        if (topic.id === topicId) {
          snapshot = snapshot || topic;
          return {
            ...topic,
            isDeleted: true,
            deletedBy: 'USER',
            deletedAt: new Date().toISOString(),
            // Also mark user as deleted for consistent UI labeling
            user: { ...topic.user, isDeleted: true }
          } as Topic;
        }
        return topic;
      }));
      // Preserve a masked copy in local cache to survive subsequent refetches
      if (snapshot) {
        softDeletedTopicsRef.current[topicId] = {
          ...snapshot,
          isDeleted: true,
          deletedBy: 'USER',
          deletedAt: new Date().toISOString(),
          user: { ...snapshot.user, isDeleted: true }
        } as Topic;
      }
    } catch (err: any) {
      console.error('Error deleting topic:', err);
      throw err;
    }
  };

  // Optimistically adjust a topic's comment count in local state
  const adjustTopicCommentCount = useCallback((topicId: string, delta: number) => {
    setTopics(prev => prev.map(topic =>
      topic.id === topicId
        ? { ...topic, commentCount: Math.max(0, (topic.commentCount || 0) + delta) }
        : topic
    ));
  }, []);

  return {
    topics,
    loading,
    error,
    meta,
    refetch: fetchTopics,
    createTopic,
    updateTopic,
    voteTopic,
    deleteTopic,
    adjustTopicCommentCount,
    markTopicSoftDeleted,
  };
};

// Custom hook for comments
export const useComments = (topicId?: string) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastTopicIdRef = useRef<string | undefined>(undefined);

  const fetchComments = useCallback(async () => {
    if (!topicId) {
      setComments([]);
      setLoading(false);
      return;
    }

    // If switching to a different topic, clear existing comments immediately to avoid showing stale ones
    if (lastTopicIdRef.current && lastTopicIdRef.current !== topicId) {
      setComments([]);
    }

    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();
    lastTopicIdRef.current = topicId;

    try {
      setLoading(true);
      setError(null);
      const response = await commentsApi.getComments({ topicId, limit: 100 });

      // Only update state if request wasn't aborted
      if (!abortControllerRef.current.signal.aborted) {
        const incoming = response.data || [];
        // If backend returns empty for a soft-deleted topic, keep showing existing comments by retaining prev state
        setComments(prev => (incoming.length === 0 && lastTopicIdRef.current === topicId ? prev : incoming));
      }
    } catch (err: any) {
      // Ignore aborted requests
      if (err.name === 'AbortError' || abortControllerRef.current?.signal.aborted) {
        return;
      }

      setError(err.response?.data?.error || err.message || 'Failed to fetch comments');
      console.error('Error fetching comments:', err);
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setLoading(false);
      }
    }
  }, [topicId]);

  useEffect(() => {
    // Add delay to prevent rapid requests when topic changes
    const timeoutId = setTimeout(() => {
      fetchComments();
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchComments]);

  const createComment = async (commentData: {
    content: string;
    topicId: string;
    parentId?: string;
    latitude?: number;
    longitude?: number;
    isAnonymous?: boolean;
  }) => {
    try {
      const newComment = await commentsApi.createComment(commentData);
      setComments(prev => [newComment, ...prev]);
      return newComment;
    } catch (err: any) {
      console.error('Error creating comment:', err);
      throw err;
    }
  };

  const voteComment = async (commentId: string, voteType: 'UPVOTE' | 'DOWNVOTE') => {
    try {
      const updatedComment = await commentsApi.voteComment(commentId, voteType);
      setComments(prev =>
        prev.map(comment => comment.id === commentId ? updatedComment : comment)
      );
      return updatedComment;
    } catch (err: any) {
      console.error('Error voting on comment:', err);
      throw err;
    }
  };

  const updateComment = async (commentId: string, data: { content?: string; [key: string]: any }) => {
    try {
      const updatedComment = await commentsApi.updateComment(commentId, data);
      setComments(prev =>
        prev.map(comment => comment.id === commentId ? { ...comment, ...updatedComment } : comment)
      );
      return updatedComment;
    } catch (err: any) {
      console.error('Error updating comment:', err);
      throw err;
    }
  };

  const deleteComment = async (commentId: string) => {
    try {
      await commentsApi.deleteComment(commentId);
      // Remove the deleted comment and any direct replies to it (single level)
      setComments(prev => prev.filter(comment => comment.id !== commentId && comment.parentId !== commentId));
    } catch (err: any) {
      console.error('Error deleting comment:', err);
      throw err;
    }
  };

  return {
    comments,
    loading,
    error,
    refetch: fetchComments,
    createComment,
    updateComment,
    voteComment,
    deleteComment,
  };
};

// Custom hook for single topic with comments
export const useTopic = (topicId?: string) => {
  const [topic, setTopic] = useState<Topic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTopic = useCallback(async () => {
    if (!topicId) {
      setTopic(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const fetchedTopic = await topicsApi.getTopic(topicId);
      setTopic(fetchedTopic);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch topic');
      console.error('Error fetching topic:', err);
    } finally {
      setLoading(false);
    }
  }, [topicId]);

  useEffect(() => {
    fetchTopic();
  }, [fetchTopic]);

  return {
    topic,
    loading,
    error,
    refetch: fetchTopic,
  };
};

// Custom hook for authentication
export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsProfile, setNeedsProfile] = useState(false);
  const [accountDeleted, setAccountDeleted] = useState(false);

  const fetchUser = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setNeedsProfile(false);
      setAccountDeleted(false);
      // Prefer Supabase session first to avoid unnecessary API calls
      if (supabase) {
        try {
          await exchangeCodeIfPresent();
          const { data } = await supabase.auth.getSession();
          const s = data.session;
          if (s?.user) {
            // Ensure our API interceptor has the user id for x-user-id header
            try { localStorage.setItem('userId', s.user.id); } catch { }
            // Fetch the authoritative user from backend so we know if username is set
            try {
              const currentUser = await authApi.getCurrentUser();
              setUser(currentUser);
              return;
            } catch (err: any) {
              const status = err?.response?.status;
              const code = err?.response?.data?.code;
              if (status === 503 && code === 'AUTH_PROVIDER_UNVERIFIED') {
                // Do not sign out on 503, as it might be a transient or config error
                // if (supabase) {
                //   try { await supabase.auth.signOut(); } catch { /* ignore */ }
                // }
                // setUser(null);
                setError('Unable to validate your sign-in. Please sign in again.');
                return;
              }
              if (status === 404 && (code === 'PROFILE_NOT_FOUND' || !code)) {
                setNeedsProfile(true);
                setUser({
                  id: s.user.id,
                  email: s.user.email || '',
                  username: undefined,
                  reputation: 0,
                  role: 'AUTHOR',
                  createdAt: new Date(s.user.created_at || Date.now()).toISOString(),
                });
                return;
              }
              if (status === 410 || code === 'SUPABASE_ACCOUNT_DELETED') {
                if (supabase) {
                  try { await supabase.auth.signOut(); } catch { /* ignore */ }
                }
                setAccountDeleted(true);
                setUser(null);
                return;
              }
              if (supabase) {
                try { await supabase.auth.signOut(); } catch { /* ignore */ }
              }
              setUser(null);
              if (status !== 401 && status !== 404) {
                setError(err?.message || 'Failed to fetch user');
                console.error('Error fetching user:', err);
              }
              return;
            }
          } else {
            setUser(null);
            return; // no Supabase session; treat as logged out without hitting backend
          }
        } catch {
          // If Supabase call fails, fall through to API check
        }
      }

      // If no Supabase session, try backend (for cookie-based auth installations)
      try {
        const currentUser = await authApi.getCurrentUser();
        setUser(currentUser);
      } catch (err: any) {
        const status = err?.response?.status;
        setUser(null);
        if (status !== 401 && status !== 404) {
          setError(err.message || 'Failed to fetch user');
          console.error('Error fetching user:', err);
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const logout = async () => {
    try {
      // Sign out from Supabase if configured
      if (supabase) {
        try { await supabase.auth.signOut(); } catch { }
      }
    } finally {
      setUser(null);
      setNeedsProfile(false);
      setAccountDeleted(false);
      localStorage.removeItem('userId');
      localStorage.removeItem('token');
      setError(null);
    }
  };

  return {
    user,
    loading,
    error,
    needsProfile,
    accountDeleted,
    logout,
    refetch: fetchUser,
  };
};
